import { useEffect, useRef, useState } from 'react';
import { ObserveSiteUsage } from '../../services/ScaleoAdminApiServices/ObserveService';
import { Station } from '../../types/station';
import { useObserveConfig } from './useObserveConfig';

const SYNC_NOTICE =
  'Site istnieje w Cobrick Observe, ale konfiguracja nie jest wysyłana automatycznie. ' +
  'Aby urządzenie brzegowe ją odebrało, wyślij nową konfigurację przez synchronizację hosta.';

export const useObserveSiteActions = (
  station: Station,
  onChanged: () => void,
  onError: (message: string | null) => void,
  onSyncNotice: (message: string | null) => void,
) => {
  const [usage, setUsage] = useState<ObserveSiteUsage | null>(null);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);

  const { downloadObserveConfig, provisionObserveSite, loadObserveSiteUsage, deleteObserveSite, busyStationId, error } =
    useObserveConfig(onChanged);

  // Held in a ref so an inline callback from the parent cannot turn this into a render loop.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => onErrorRef.current(error), [error]);

  // Held in a ref for the same reason as onError above.
  const onSyncNoticeRef = useRef(onSyncNotice);
  onSyncNoticeRef.current = onSyncNotice;

  const hasSite = Boolean(station.observeSite);
  const busy = busyStationId === station.id;

  const openCreate = () => setCreateVisible(true);

  const closeCreate = () => setCreateVisible(false);

  const createSite = async () => {
    onSyncNoticeRef.current(null);
    setCreateVisible(false);

    if (await provisionObserveSite(station)) {
      onSyncNoticeRef.current(SYNC_NOTICE);
    }
  };

  const downloadConfig = () => {
    onSyncNoticeRef.current(hasSite ? SYNC_NOTICE : null);
    void downloadObserveConfig(station);
  };

  const openDelete = async () => {
    const loaded = await loadObserveSiteUsage(station);

    if (!loaded?.siteName) {
      return;
    }

    setUsage(loaded);
    setDeleteVisible(true);
  };

  const closeDelete = () => {
    setDeleteVisible(false);
    setUsage(null);
  };

  const confirmDelete = async () => {
    if (await deleteObserveSite(station)) {
      closeDelete();
    }
  };

  return {
    hasSite,
    busy,
    usage,
    createVisible,
    deleteVisible,
    openCreate,
    closeCreate,
    createSite,
    downloadConfig,
    openDelete,
    closeDelete,
    confirmDelete,
  };
};
